import { calculateIncomeTax, incomeTaxRulesFor } from "../income-tax/index.js";
import type { FinancialYear } from "../types.js";
import {
  type CgtOnSaleInput,
  CgtOnSaleInputSchema,
  type CostBaseParsed,
  type CgtOnSaleOutput,
  type CgtRuleSet,
  type LossApplicationInput,
  LossApplicationInputSchema,
  type LossApplicationOutput,
  type WashSaleInput,
  WashSaleInputSchema,
  type WashSaleOutput,
  type WashSalePrecondition,
} from "./types.js";

// ---------------------------------------------------------------------------
// CGT calc surfaces. Pure: inputs + ruleset always yield the same output; no
// I/O, no Date.now() (the assessment FY AND the contract dates are parameters).
// Zod validates at the boundary so malformed payloads fail loudly.
//
// The marginal-rate tax on the net gain is NOT re-derived here — every gain is
// taxed via the shared `income-tax` engine, stacked on top of the user's other
// taxable income (`tax(other + netGain) − tax(other)`), which is the EXTRA tax
// the gain causes. There is no separate/flat CGT rate (ATO, CITED).
//
// Every personalised result passes `assertNoFinancialProductHandoff` before it
// leaves — the C2 AFSL kill-line (no broker/trading-platform/exchange handoff on
// a personalised CGT surface) is enforced in code, not just doc prose.
// ---------------------------------------------------------------------------

/**
 * HARD ARCHITECTURAL RULE (panel cgt-on-sale lens 2 + cgt-wash-sale-checker
 * lens 2, made binding): a personalised CGT result routed to a paid
 * financial-product handoff — share/crypto trading platform, brokerage, or
 * exchange — is a s764A financial-product paid handoff from a personalised
 * surface (general advice / dealing-by-arranging, INFO 269 / RG 36 §D). That is
 * the brand's #1 kill-line. This guard runs over every result's serialised form
 * and throws if a broker/platform/exchange/CTA/advice identifier leaks in. Tax
 * software is the only clean lane and is footer/resources-only, off the result
 * line — so it must never appear on a result object either.
 */
const FORBIDDEN_HANDOFF_TERMS = [
  "broker",
  "brokerage",
  "trading platform",
  "trade now",
  "buy now",
  "sell now",
  "exchange",
  "stake",
  "pearler",
  "swyftx",
  "coinspot",
  "binance",
  "coinbase",
  "open an account",
  "start trading",
  "we recommend",
  "you should sell",
  "you should hold",
  "minimise your cgt",
  "minimise your tax",
  "reduce your cgt",
  "save tax",
  "save $",
];

/** Thrown when a forbidden financial-product handoff / advice term reaches a
 * personalised CGT result surface. */
export class CgtHandoffError extends Error {
  constructor(term: string) {
    super(
      `CGT result leaked a forbidden financial-product handoff / advice term ` +
        `(C2 AFSL kill-line): "${term}" — a personalised CGT surface must not ` +
        `route to any broker / trading platform / crypto exchange, nor carry ` +
        `"sell/minimise/save tax" advice copy.`,
    );
    this.name = "CgtHandoffError";
  }
}

export function assertNoFinancialProductHandoff(output: unknown): void {
  const serialised = JSON.stringify(output).toLowerCase();
  for (const term of FORBIDDEN_HANDOFF_TERMS) {
    if (serialised.includes(term)) {
      throw new CgtHandoffError(term);
    }
  }
}

const round = (n: number) => Math.round(n);

// ===========================================================================
// Date / holding-period helpers — contract-date to contract-date, NOT
// settlement. The >12-month test EXCLUDES both endpoints (ATO method): the
// asset must be held a minimum of 367 calendar days (368 in a leap year), i.e.
// the count of days strictly BETWEEN the two contract dates must exceed 365.
// ===========================================================================

/** Parse an ISO yyyy-mm-dd into a UTC timestamp (midnight UTC) — avoids any
 * local-timezone drift, keeping the function pure and deterministic. */
function parseIsoDateUtc(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) throw new Error(`CGT: invalid ISO date "${iso}" (expected yyyy-mm-dd)`);
  const [, y, mo, d] = m;
  const ts = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  // Guard against rollover (e.g. 2025-02-30 → March): round-trip must match.
  const back = new Date(ts);
  if (
    back.getUTCFullYear() !== Number(y) ||
    back.getUTCMonth() !== Number(mo) - 1 ||
    back.getUTCDate() !== Number(d)
  ) {
    throw new Error(`CGT: invalid calendar date "${iso}"`);
  }
  return ts;
}

const MS_PER_DAY = 86_400_000;

/**
 * Days held EXCLUDING both endpoints (the count of full days strictly between
 * the acquisition and disposal contract dates). For a span of `N` calendar days
 * (inclusive of both ends) this is `N − 1`. Throws if disposal precedes
 * acquisition.
 */
export function daysHeldExclusive(
  acquisitionContractDate: string,
  disposalContractDate: string,
): number {
  const a = parseIsoDateUtc(acquisitionContractDate);
  const d = parseIsoDateUtc(disposalContractDate);
  const calendarSpan = Math.round((d - a) / MS_PER_DAY);
  if (calendarSpan < 0) {
    throw new Error(
      `CGT: disposal contract date ${disposalContractDate} precedes acquisition ${acquisitionContractDate}`,
    );
  }
  // Exclude both endpoints: subtract 1 from the inclusive day span.
  return Math.max(0, calendarSpan - 1);
}

/** Full cost base (all five elements) — used to work out a capital GAIN. */
export function fullCostBase(c: CostBaseParsed): number {
  return (
    c.acquisitionCost +
    c.incidentalCosts +
    c.ownershipCosts +
    c.capitalImprovementCosts +
    c.titleCosts
  );
}

/** Reduced cost base — EXCLUDES element 3 (costs of owning). Used to work out a
 * capital LOSS (ATO: a loss never includes ownership costs). */
export function reducedCostBase(c: CostBaseParsed): number {
  return (
    c.acquisitionCost +
    c.incidentalCosts +
    c.capitalImprovementCosts +
    c.titleCosts
  );
}

/**
 * Marginal income-tax + Medicare attributable to a net capital gain stacked on
 * top of `otherTaxableIncome`: `tax(other + gain) − tax(other)`. Never taxes the
 * gain in isolation (the error free CGT calculators routinely make). Also
 * returns the HELP/STSL repayment delta the gain pulls and the marginal rate.
 */
function marginalTaxOnGain(
  otherTaxableIncome: number,
  netCapitalGain: number,
  hasStudyLoan: boolean,
  fy: FinancialYear,
): { taxOnGain: number; helpRepaymentDelta: number; marginalRate: number } {
  const rules = incomeTaxRulesFor(fy);
  const baseline = calculateIncomeTax(
    {
      taxableIncome: otherTaxableIncome,
      residency: "resident",
      hasStudyLoan,
      assessmentYear: fy,
    },
    rules,
  );
  const withGain = calculateIncomeTax(
    {
      taxableIncome: otherTaxableIncome + netCapitalGain,
      residency: "resident",
      hasStudyLoan,
      assessmentYear: fy,
    },
    rules,
  );
  // The CGT figure is income tax (after offsets) + Medicare — i.e. netTax delta.
  const taxOnGain = Math.max(0, withGain.netTax - baseline.netTax);
  const helpRepaymentDelta = Math.max(
    0,
    withGain.helpRepayment - baseline.helpRepayment,
  );
  return { taxOnGain, helpRepaymentDelta, marginalRate: withGain.marginalRate };
}

// ===========================================================================
// cgtOnSale — cost base → gain → 12-month discount → marginal-rate tax.
// ===========================================================================

/**
 * CGT on a single-parcel sale for a resident individual. ORDER (ATO, CITED):
 *   1. cost base (five elements) → gross capital gain (or loss on reduced base)
 *   2. >12-month test, contract-to-contract, both endpoints excluded
 *   3. apply the 50% discount to the gain IF eligible → net capital gain
 *   4. tax the net gain at the MARGINAL rate, stacked on other income, via the
 *      shared income-tax engine
 * Pure. Validates at the boundary. The result passes the no-handoff guard.
 */
export function cgtOnSale(
  rawInput: CgtOnSaleInput,
  rules: CgtRuleSet,
): CgtOnSaleOutput {
  const input = CgtOnSaleInputSchema.parse(rawInput);
  const fy = input.assessmentYear as FinancialYear;

  const costBase = fullCostBase(input.costBase);
  const rcb = reducedCostBase(input.costBase);

  // A gain uses the full cost base; a loss uses the reduced cost base. They
  // cannot both be non-zero.
  const grossCapitalGain = Math.max(0, input.proceeds - costBase);
  const capitalLoss =
    grossCapitalGain > 0 ? 0 : Math.max(0, rcb - input.proceeds);

  const daysHeld = daysHeldExclusive(
    input.acquisitionContractDate,
    input.disposalContractDate,
  );
  // >12-month test: held strictly more than minDaysHeldForDiscount (365) days,
  // both endpoints already excluded from daysHeld.
  const discountEligible =
    grossCapitalGain > 0 && daysHeld > rules.minDaysHeldForDiscount;
  const discountRate = discountEligible ? rules.discountRate : 0;

  // Discount is applied to the GAIN (after any losses — but cgtOnSale is the
  // single-disposal surface with no losses; loss-ordering lives in
  // `lossApplicationOrder`, which proves the losses-BEFORE-discount order).
  const netCapitalGain = round(grossCapitalGain * (1 - discountRate));

  const { taxOnGain, helpRepaymentDelta, marginalRate } = marginalTaxOnGain(
    input.otherTaxableIncome,
    netCapitalGain,
    input.hasStudyLoan,
    fy,
  );

  const out: CgtOnSaleOutput = {
    costBase,
    reducedCostBase: rcb,
    proceeds: input.proceeds,
    grossCapitalGain,
    capitalLoss,
    daysHeld,
    discountEligible,
    discountRate,
    netCapitalGain,
    taxOnGain,
    marginalRate,
    helpRepaymentDelta,
    isEstimate: true,
  };
  assertNoFinancialProductHandoff(out);
  return out;
}

// ===========================================================================
// lossApplicationOrder — losses BEFORE the discount, current-year then
// carried-forward, non-discountable gains first. Tracks carry-forward.
// ===========================================================================

/**
 * Apply capital losses in the ATO order (CITED — the single sharpest accuracy
 * risk; mis-ordering over-claims the discount and is misleading-conduct
 * exposure):
 *   - losses are subtracted from the GROSS gains BEFORE the 50% discount;
 *   - current-year losses first, THEN carried-forward losses;
 *   - applied to NON-discountable gains before discountable gains (the
 *     lowest-CGT result — burning a full dollar of loss against a half-taxed
 *     discount gain wastes it);
 *   - the 50% discount is applied to whatever discountable gain REMAINS;
 *   - unused losses carry forward indefinitely (offset capital gains only,
 *     never ordinary income).
 * Pure. The result passes the no-handoff guard.
 */
export function lossApplicationOrder(
  rawInput: LossApplicationInput,
  rules: CgtRuleSet,
): LossApplicationOutput {
  const input = LossApplicationInputSchema.parse(rawInput);

  const totalLossesAvailable =
    input.currentYearLosses + input.carriedForwardLosses;

  // Apply losses BEFORE the discount, non-discountable bucket first (best
  // result). Current-year-then-carried-forward only affects which pool the
  // carry-forward draws from; for the netting math the order within "apply to
  // non-discountable first, then discountable" is what changes the answer.
  let remainingLosses = totalLossesAvailable;

  const lossOnNonDiscountable = Math.min(
    remainingLosses,
    input.nonDiscountableGains,
  );
  remainingLosses -= lossOnNonDiscountable;
  const nonDiscountableAfterLosses =
    input.nonDiscountableGains - lossOnNonDiscountable;

  const lossOnDiscountable = Math.min(remainingLosses, input.discountableGains);
  remainingLosses -= lossOnDiscountable;
  const discountableAfterLosses = input.discountableGains - lossOnDiscountable;

  const lossesApplied = lossOnNonDiscountable + lossOnDiscountable;

  // Discount applied to the REMAINING discountable gain (after losses).
  const netCapitalGain = round(
    nonDiscountableAfterLosses +
      discountableAfterLosses * (1 - input.discountRate),
  );

  // Unused losses carry forward. They offset capital gains only and never
  // reduce ordinary income, so they survive even when there is no other gain.
  const carryForwardToNextYear = remainingLosses;

  const out: LossApplicationOutput = {
    totalLossesAvailable,
    lossesApplied,
    nonDiscountableAfterLosses,
    discountableAfterLosses,
    netCapitalGain,
    carryForwardToNextYear,
    hasCarryForward: carryForwardToNextYear > 0,
    isEstimate: true,
  };
  // `rules` is accepted for signature symmetry / future FY-specific loss rules;
  // current FY has none beyond the discount rate already carried on the input.
  void rules;
  assertNoFinancialProductHandoff(out);
  return out;
}

// ===========================================================================
// washSaleFlag — neutral informational warning. NEVER a clearance, never advice.
// ===========================================================================

/**
 * Flag the wash-sale anti-avoidance pattern (sell at a loss + reacquire a
 * substantially identical asset with the dominant purpose of crystallising the
 * loss) as a NEUTRAL informational warning. There is NO US-style fixed window —
 * TA 2008/7 is a dominant-purpose / Part IVA test, so no output can promise
 * safety; `noFixedTimeWindow` is always true. This is information about a rule,
 * NOT advice to sell/hold/harvest/re-buy and NOT a ruling on the user's
 * situation. Pure. The result passes the no-handoff guard.
 */
export function washSaleFlag(rawInput: WashSaleInput): WashSaleOutput {
  const input = WashSaleInputSchema.parse(rawInput);

  const missing: WashSalePrecondition[] = [];
  if (!input.soldAtLoss) missing.push("sold-at-loss");
  if (!input.reacquiredSameOrSubstantiallyIdentical) {
    missing.push("reacquired-same-or-substantially-identical");
  }
  if (!input.dominantPurposeTaxBenefit) {
    missing.push("dominant-purpose-tax-benefit");
  }

  const patternPresent = missing.length === 0;

  const out: WashSaleOutput = {
    risk: patternPresent ? "elevated" : "not-indicated",
    patternPresent,
    missingPreconditions: missing,
    noFixedTimeWindow: true,
    isInformationalWarning: true,
  };
  assertNoFinancialProductHandoff(out);
  return out;
}
